/**
 * ImageCacheService — Préfetch intelligent d'images pour améliorer le scroll
 *
 * Utilisation :
 *   import { prefetchImages } from './ImageCacheService';
 *   prefetchImages(products.slice(0, 20).map(p => p.image));
 *
 * Évite de re-prefetch la même URL 2 fois.
 * Limite à 20 prefetch parallèles pour ne pas saturer le réseau.
 */
import { Image } from 'react-native';

// URL déjà préfetchées (mémoire uniquement, reset au restart)
const prefetched = new Set();
const inProgress = new Set();

// File d'attente pour throttling
const queue = [];
let activeCount = 0;
const MAX_PARALLEL = 5;

function processQueue() {
  while (activeCount < MAX_PARALLEL && queue.length > 0) {
    const uri = queue.shift();
    if (!uri || prefetched.has(uri) || inProgress.has(uri)) continue;

    inProgress.add(uri);
    activeCount++;

    Image.prefetch(uri)
      .then(() => {
        prefetched.add(uri);
      })
      .catch(() => {
        // Silencieux : image cassée ou réseau down
      })
      .finally(() => {
        inProgress.delete(uri);
        activeCount--;
        // Continuer la queue
        processQueue();
      });
  }
}

/**
 * Préfetch une liste d'URLs d'images.
 * @param {string[]} uris
 * @param {Object} opts
 * @param {number} opts.priority - 'high' (unshift) | 'low' (push, default)
 */
export function prefetchImages(uris, opts = {}) {
  if (!Array.isArray(uris)) return;
  const valid = uris.filter(u => typeof u === 'string' && u.startsWith('http') && !prefetched.has(u));

  if (opts.priority === 'high') {
    queue.unshift(...valid);
  } else {
    queue.push(...valid);
  }

  processQueue();
}

/**
 * Vérifier si une image est déjà en cache.
 */
export function isCached(uri) {
  return prefetched.has(uri);
}

/**
 * Vider le cache interne (reset state uniquement, pas le cache disque RN)
 */
export function clearCache() {
  prefetched.clear();
  inProgress.clear();
  queue.length = 0;
  activeCount = 0;
}

/**
 * Stats pour debug
 */
export function getCacheStats() {
  return {
    prefetched: prefetched.size,
    inProgress: inProgress.size,
    queueLength: queue.length,
  };
}
